import { FC, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  DateRangePicker,
  InputSelect,
  RadioGroup,
  RadioGroupItem,
} from '@features/ui';
import {
  payoutCheckboxData,
  payoutRadioData,
} from '@features/admin/payoutRequests/payoutData.ts';
import { DateValueType } from '@lib/utils.ts';
import dayjs from 'dayjs';
import {
  downloadPayoutReport,
  generatePayoutReport,
} from '@src/services/payout';
import { toast } from 'react-toastify';

const payoutStatusOptions = [
  { label: 'Paid', value: 'paid' },
  { label: 'Approved, pending payment', value: 'approved' },
  { label: 'Unpaid', value: 'unpaid' },
];

const payoutMethodOptions = [
  { label: 'All', value: 'all' },
  { label: 'Rise only', value: 'riseworks' },
  { label: 'Wire only', value: 'wire' },
  { label: 'Paypal only', value: 'paypal' },
  { label: 'Coupon (credit)', value: 'coupon' },
];

export const PayoutReporting: FC = () => {
  const [payoutStatus, setPayoutStatus] = useState<string>();
  const [payoutMethod, setPayoutMethod] = useState<string>('all');
  const [radioSelected, setRadioSelected] = useState<string>('rb_pa');
  const [checkboxSelected, setCheckboxSelected] = useState<string[]>([]);
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);
  const [loading, setLoading] = useState(false);
  const [reportResult, setReportResult] = useState<{
    count: number;
    download_url: string;
    message: string;
  } | null>(null);

  const handleCheckboxChange = (value: string) => {
    setCheckboxSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const buildPayload = () => {
    // Map checkbox values to API params
    const checkboxMapping: Record<string, string> = {
      cb_wire_pdf: 'include_wire_pdfs',
      cb_w9: 'include_w9',
      cb_w8: 'include_w8',
    };

    const includeParams = checkboxSelected.reduce(
      (acc, value) => {
        const apiKey = checkboxMapping[value];

        if (apiKey) {
          acc[apiKey] = true;
        }

        return acc;
      },
      {} as Record<string, boolean>
    );

    return {
      created_from: valueStart?.startDate
        ? dayjs(valueStart.startDate).toISOString()
        : undefined,
      created_to: valueEnd?.startDate
        ? dayjs(valueEnd.startDate).toISOString()
        : undefined,
      status: payoutStatus, // 'approved', 'unpaid', 'paid'
      payment_type: payoutMethod || 'all', // 'all', 'riseworks', 'wire', 'paypal', 'coupon'
      is_pa: radioSelected === 'rb_pa', // true for PA (rb_pa), false for Completion (rb_completion)
      ...includeParams, // include_wire_pdfs, include_w9, include_w8
    };
  };

  const handleFilterPayout = async () => {
    if (!valueStart?.startDate || !valueEnd?.startDate) {
      toast.error('Please select both start and end dates');

      return;
    }

    const payload = buildPayload();

    try {
      setLoading(true);
      setReportResult(null); // Clear previous result
      const response = await generatePayoutReport(payload);

      if (response.data.download_url) {
        // Store result to display
        setReportResult(response.data);
        toast.success(response.data.message || 'Report generated successfully');
      } else {
        toast.info(response.data.message || 'No data available for download');
      }
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        'Failed to generate report';

      toast.error(message);
      setReportResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportResult?.download_url) return;

    try {
      setLoading(true);
      const response = await downloadPayoutReport(reportResult.download_url);

      const contentType =
        response.headers['content-type'] || 'application/octet-stream';

      const blob = new Blob([response.data], {
        type: contentType,
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'payout_report.zip';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );

        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      if (filename === 'payout_report.zip' && reportResult.download_url) {
        const urlParts = reportResult.download_url.split('/');
        const urlFilename = urlParts[urlParts.length - 1];

        if (urlFilename && urlFilename.includes('.')) {
          filename = urlFilename;
        }
      }

      link.download = filename;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        'Failed to download report';

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className={'text-xs flex flex-col gap-y-7'}>
        <p>
          You can prepare a report of payouts here, pick your dates (date payout
          was requested) and download options
        </p>
        <div className={'flex justify-between lg:gap-x-12.5 gap-x-5'}>
          <div className={'flex-1 flex flex-col gap-y-9'}>
            <DateRangePicker
              asSingle={true}
              size={'sm'}
              value={valueStart}
              variant={'outline'}
              onChange={setValueStart}
            />
            <DateRangePicker
              asSingle={true}
              size={'sm'}
              value={valueEnd}
              variant={'outline'}
              onChange={setValueEnd}
            />
          </div>
          <div className={'flex-1 flex flex-col gap-y-9'}>
            <InputSelect
              className={'min-w-[239px]'}
              options={payoutStatusOptions}
              placeholder={'Payout Status'}
              value={payoutStatus}
              onChange={setPayoutStatus}
            />
            <InputSelect
              className={'min-w-[239px]'}
              options={payoutMethodOptions}
              placeholder={'Payout Method'}
              value={payoutMethod}
              onChange={setPayoutMethod}
            />
          </div>
          <div className={'flex-1 flex flex-col gap-2'}>
            <p>Payout Status</p>
            <RadioGroup
              defaultValue={radioSelected}
              onValueChange={(value) => setRadioSelected(value)}
            >
              {payoutRadioData.map((item) => {
                return (
                  <div key={item.value} className={'flex items-center gap-x-2'}>
                    <RadioGroupItem value={item.value} />
                    <p>{item.label}</p>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
          <div className={'flex-1 flex flex-col gap-2'}>
            <p>Download to include</p>
            {payoutCheckboxData.map((item) => (
              <Checkbox
                key={item.value}
                checked={checkboxSelected.includes(item.value)}
                className="w-[15px] h-[14px] border-2 border-white"
                id={`checkbox-${item.value}`}
                label={item.label}
                onCheckedChange={() => handleCheckboxChange(item.value)}
              />
            ))}
          </div>
          <div className={'flex h-auto items-center'}>
            <Button
              className={'w-[58px]'}
              loading={loading}
              size={'sm'}
              variant={'lime'}
              onClick={handleFilterPayout}
            >
              Go
            </Button>
          </div>
        </div>
      </Card>

      {/* Result display */}
      {reportResult && (
        <Card className={'text-xs flex flex-col gap-y-4 items-center mt-4'}>
          <p className={'text-lg font-medium'}>
            Payouts Found: {reportResult?.count}
          </p>
          <Button size={'sm'} variant={'lime'} onClick={handleDownloadReport}>
            Download Report
          </Button>
        </Card>
      )}
    </>
  );
};
